(ns hai.home.onboarding
  "Tips and tricks state: which setup steps the user has stamped done, and
   whether the mobile tour has opened by itself yet. Persists per user in
   `:user-config/onboarding`, which rides along on `:auth/identity`.

   A stamp records only that the user is finished with a step — it never acts
   on their behalf. The home-page step carries a real switch for
   `:user-config.newtab/override?`, but its box is separate state, so someone
   who means to skip that step can stamp it without the new-tab setting
   changing under them."
  (:require
   [hai.onboarding :as onboarding]
   [hai.ui.rf-sugar :refer [defevent-fx defsub]]))

;; ---------------------------------------------------------------------------
;; Reads
;; ---------------------------------------------------------------------------

(defsub ::progress
  [identity <- :auth/identity]
  (:user-config/onboarding identity))

;; Every step either surface shows; each card counts only its own `steps`.
(defsub ::done
  [progress <- ::progress]
  (reduce (fn [acc {:keys [key]}] (assoc acc key (boolean (get progress key))))
          {}
          (distinct (concat onboarding/mobile-steps onboarding/desktop-steps))))

(defsub ::remaining
  [steps
   done :<- ::done]
  (count (remove #(get done (:key %)) steps)))

;; When the mobile tour first opened on its own, as epoch ms: a plain number
;; rides the transit-encoded progress map unchanged, where a date would be
;; re-typed on the way through. Set however the tour then ends — finished,
;; closed with the X, or the page left mid-way — so it opens by itself only
;; once, ever; the Tips and tricks card's step is the way back in.
(def ^:private mobile-tour-seen-key :onboarding.mobile-tour/seen-at)

(defn mobile-tour-seen?
  [db]
  (some? (get-in db [:auth/identity :user-config/onboarding mobile-tour-seen-key])))

;; The live setting behind the home-page step's switch — its own state, not the
;; step's stamp.
(defsub ::homepage-override?
  [identity <- :auth/identity]
  (boolean (:user-config.newtab/override? identity)))

;; ---------------------------------------------------------------------------
;; Writes
;; ---------------------------------------------------------------------------

;; Datomic omits the attr until the first stamp, so this is nil for a new user
;; — and `into` onto nil would build a list rather than a map.
(defn- progress-of
  [db]
  (or (get-in db [:auth/identity :user-config/onboarding]) {}))

(defn- stamp
  "Store `progress` — the whole map, not a delta — locally and on the server."
  [db progress]
  {:db (assoc-in db [:auth/identity :user-config/onboarding] progress)
   :resolve {:query `[(~'user/save-onboarding ~{:onboarding/progress progress})]}})

(defevent-fx ::toggle-step
  [{:keys [db]} step-key]
  (stamp db (update (progress-of db) step-key not)))

;; Stamps one step done — never un-stamps, unlike [[::toggle-step]]. For a
;; step the user finishes elsewhere (the mobile tour, on its last screen).
(defevent-fx ::complete-step
  [{:keys [db]} step-key]
  (stamp db (assoc (progress-of db) step-key true)))

(defevent-fx ::mark-mobile-tour-seen
  [{:keys [db]}]
  (stamp db (assoc (progress-of db) mobile-tour-seen-key (.now js/Date))))

;; Stamps whatever is still open of the card's `steps`. Stamps act on nothing,
;; so this changes no setting either — and it leaves the card up.
(defevent-fx ::mark-all-done
  [{:keys [db]} steps]
  (stamp db (into (progress-of db) (map (juxt :key (constantly true))) steps)))
